import type { Experiment } from 'types';
import { useState } from 'react';
import { useExperimentReport } from '@/state/experiments/useExperimentReport';
import { useReportRead } from '@/state/experiments/useReportRead';
import { AnswersDrawer } from './AnswersDrawer';
import { buildContext } from './report-context';
import { ReportCard } from './ReportCard';
import { summaryFor } from './ReportForm';
import { SectionAuthor } from '../SectionAuthor';

type ExperimentReportScreenProps = { experiment: Experiment };

const formatSaved = (timestamp: number) => {
  const date = new Date(timestamp);
  const day = date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return `saved ${day} · ${time}`;
};

/**
 * The after action report for a concluded experiment, read first: a verdict card with the
 * platform's facts beside what the team said, and the answers in a drawer underneath. By the
 * time anyone opens the tab the form has been filled in, and the learning is what they came for.
 *
 * Anyone with access to the experiment can fill it in or change it at any time, which is how
 * experiments that ended on a schedule or in a pipeline get theirs. The drawer starts closed
 * when there is a report to read and open when there is not, since there is nothing to read yet.
 */
export const ExperimentReportScreen = ({ experiment }: ExperimentReportScreenProps) => {
  const manager = useExperimentReport(experiment.id);
  const { read } = useReportRead({ experimentId: experiment.id, refresh: true });
  const { savedAt, hasAnswers, isLoading, isDirty, discard, save } = manager;

  // null means the user has not chosen, so it follows whether there is anything to read.
  const [chosen, setChosen] = useState<boolean | null>(null);
  const isOpen = chosen ?? !hasAnswers;

  if (isLoading) return <p className="supporting">Loading the debrief…</p>;

  const onCancel = () => {
    discard();
    setChosen(false);
  };

  const onSave = async () => {
    if (await save()) setChosen(false);
  };

  const summary = summaryFor({ manager, context: buildContext(experiment), read });

  return (
    <section className="bg-panel border-line rounded-panel overflow-hidden border">
      <div className="bg-panel2 border-line flex flex-wrap items-center justify-between gap-3.5 border-b px-3.5 py-[13px]">
        <div className="flex flex-col gap-[3px]">
          <h2 className="micro-label text-foreground">After action report</h2>
          <p className="supporting">What happened with this test and why.</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Who started the report. The card's byline below names the last author, which is a
              different fact, so both stay. */}
          <SectionAuthor experiment={experiment} section="debrief" hasContent={hasAnswers} />
          <span className="text-dim font-mono text-[10px]">
            {isDirty ? 'unsaved changes' : savedAt ? formatSaved(savedAt) : 'not saved yet'}
          </span>
        </div>
      </div>

      <ReportCard summary={summary} />

      <AnswersDrawer
        manager={manager}
        open={isOpen}
        onToggle={() => setChosen(!isOpen)}
        onCancel={onCancel}
        onSave={onSave}
      />
    </section>
  );
};
